import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { map, startWith, take, takeUntil } from 'rxjs/operators';

import { AvailableLanguages } from '@constants';
import { AllDeviceInfo, ConsumableCountry } from '@interfaces';
import { CommonsFacade } from '@state/commons';
import { ReplaySubject } from 'rxjs';
import { DeviceInfoService } from '@core/services/device-info.service';
import { LayoutService } from '@layout/layout.service';

export interface CountrySelectOption {
  value: string;
  label: string;
}

@Component({
  selector: 'app-country-select',
  templateUrl: './country-select.component.html',
  styleUrls: ['./country-select.component.scss'],
})
export class CountrySelectComponent implements OnInit, OnDestroy {
  private destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  @ViewChild('countrySelect') countrySelect: ElementRef;

  @Input() fieldControl: FormControl;
  @Input() fieldKey: string;
  @Input() fieldLabelKey: string;
  @Input() isRequired: boolean;
  // Array of pseudoId -> you can find those in api -> modules -> common -> countries.ts
  @Input() filterOutIds?: number[];
  @Input() onlyUE?: boolean;

  public deviceInfo: AllDeviceInfo;

  public countriesList: CountrySelectOption[];
  public currentLang: string;
  private rawCountries: ConsumableCountry[];

  constructor(
    private readonly commonsFacade: CommonsFacade,
    private readonly translateService: TranslateService,
    private readonly deviceInfoService: DeviceInfoService,
    private readonly layoutService: LayoutService
  ) {}

  public async ngOnInit(): Promise<void> {
    this.deviceInfo = this.deviceInfoService.getInfo();

    this.currentLang = this.translateService.currentLang;
    if (this.onlyUE) {
      this.rawCountries = await this.commonsFacade
        .getCountriesEU$()
        .pipe(
          take(1),
          map(countriesEU => {
            if (this.filterOutIds?.length) {
              return countriesEU.filter(countriesEU => !this.filterOutIds.includes(countriesEU.id));
            } else {
              return countriesEU;
            }
          })
        )
        .toPromise();
    } else {
      this.rawCountries = await this.commonsFacade
        .getCountries$()
        .pipe(
          take(1),
          map(countries => {
            if (this.filterOutIds?.length) {
              return countries.filter(country => !this.filterOutIds.includes(country.id));
            } else {
              return countries;
            }
          })
        )
        .toPromise();
    }

    this.translateService.onLangChange
      .pipe(startWith({ lang: this.currentLang }), takeUntil(this.destroy$))
      .subscribe(({ lang: langKey }) => {
        const langKeyTyped = langKey as AvailableLanguages;
        this.countriesList = this.rawCountries.map(consumableCountry => ({
          value: consumableCountry.name,
          label: consumableCountry.nameTranslations[langKeyTyped],
        }));
        this.currentLang = langKey;
      });
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  public onOpen(): void {
    if (this.deviceInfo.deviceTypeDetected !== 'DESKTOP') {
      setTimeout(() => {
        const container = document.querySelector('.content');
        const target = this.countrySelect.nativeElement;

        const containerTop = container.getBoundingClientRect().top;
        const targetTop = target.getBoundingClientRect().top;

        this.layoutService.scrollContentTo$.next({
          top: container.scrollTop + (targetTop - containerTop - 25),
          behavior: 'smooth',
        });
      }, 200);
    }
  }
}
